import React, { useEffect, useState, useContext, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import '../pages.css';
import {
  MButton,
  LoadingContext,
  M_StateActions,
  MHeader,
  NavigateContext
} from 'src/M_Components';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { BufferService } from 'src/M_Components/M_Utils/buffer.service';
import { isFailurePayload, apiMessage } from 'src/components/utils/apiResponse';
import DynamicForm from '../../components/Forms/DynamicForm';

// =================================================================================================================
// Helpers
// =================================================================================================================

//=====================================================================================================================================
// toCamel
// Converts a string to camelCase for payload keys derived from Options metadata.
//=====================================================================================================================================
const toCamel = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

//=====================================================================================================================================
// isBoolean
// Returns true when an option is a boolean type. Used by payload builder.
//=====================================================================================================================================
const isBoolean = (opt) => opt.Type === 'boolean' || opt.Type === 'bool';

// =====================================================================================================================================
// --------------- COMPONENT -----------------------
// =====================================================================================================================================
export default function ContactConfigCrud({ handleTabClose, toastMessages }) {
  const dispatch = useDispatch();
  const M_State = useSelector((state) => state.StateKeyValue);
  const { t, i18n } = useTranslation();
  const ctx = useContext(LoadingContext);
  const { renameTab } = useContext(NavigateContext);

  const selectedTab = M_State.find((o) => o.key === 'selectedTab')?.value;

  const globalOperation = M_State.find((obj) => obj.key === 'operation')?.value;
  const globalLine = M_State.find((obj) => obj.key === 'ContactLine')?.value;

  const [id, setId] = useState(M_State.find((obj) => obj.key === 'selectedTab')?.value);
  const [operation, setOperation] = useState(globalOperation);
  const [sessionLine, setSessionLine] = useState(globalLine);

  const isViewMode = (operation || '').toLowerCase() === 'view';

  // Form state and Options for DynamicForm
  const [contactInfo, setContactInfo] = useState({});
  const [allOptions, setAllOptions] = useState([]);   // raw Options fed to DynamicForm
  const [isFirst, setIsFirst] = useState(true);
  const [contactId, setContactId] = useState(sessionLine?.Id);

  // Imperative ref to DynamicForm
  const formRef = useRef(null);

  // Stale-closure guards
  const idRef = useRef(id);
  const operationRef = useRef(operation);
  const contactIdRef = useRef(contactId);
  useEffect(() => { idRef.current = id; }, [id]);
  useEffect(() => { operationRef.current = operation; }, [operation]);
  useEffect(() => { contactIdRef.current = contactId; }, [contactId]);

  // Async race guard between tab switches
  const tabTokenRef = useRef(0);
  useEffect(() => { tabTokenRef.current += 1; }, [id]);

  // services
  const svcContacts = useMemo(() => new BufferService('Contacts', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
  const svcCreate = useMemo(() => new BufferService('Contacts/Create', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
  const svcUpdate = useMemo(() => new BufferService('Contacts/Update', ctx.DEFAULTURL), [ctx.DEFAULTURL]);

  //=====================================================================================================================================
  // Updates the tab label whenoperation or display name changes.
  // Uses sessionLine.Name first, then contactInfo.Name, then base label.
  //=====================================================================================================================================
  useEffect(() => {
    if (!selectedTab || !operation) return;
    const key =
      operation === 'Create' ? 'LabelNewContact' : operation === 'Edit' ? 'LabelEditContact' : 'LabelViewContact';
    const name = sessionLine?.Name ?? contactInfo?.Name ?? '';
    const base = t(key);
    const label = operation === 'Create' || !name ? base : `${base} "${name}"`;
    renameTab(String(selectedTab), label);
  }, [selectedTab, operation, sessionLine?.Name, contactInfo?.Name]);

  //=====================================================================================================================================
  // On mount:
  // 1) Restore per-tab snapshot (contactInfo, operation, sessionLine, allOptions).
  // 2) Consume one-shot global M_State to keep tabs isolated.
  // 3) Fetch Options for DynamicForm; then conditionally fetch entity details.
  //=====================================================================================================================================
  useEffect(() => {
    const sessionForThisTab = M_State.find((o) => o.key === id)?.value || [];
    const pick = (k) => sessionForThisTab.find((x) => x.key === k)?.value;

    const snapInfo = pick('contactInfo');
    const snapIF = pick('isFirst');
    const snapOP = pick('operation');
    const snapSL = pick('sessionLine');
    const snapOptions = pick('allOptions') ?? pick('formSchema'); // backward compatibility
    const snapContactId = pick('contactId');

    if (snapInfo) setContactInfo(snapInfo);
    if (snapIF !== undefined) setIsFirst(snapIF);
    else if (isFirst) setIsFirst(false);

    const seedOperation = globalOperation !== undefined ? globalOperation : snapOP;
    const seedLine = globalLine !== undefined ? globalLine : snapSL;

    if (seedOperation !== undefined) setOperation(seedOperation);
    if (seedLine) {
      setSessionLine(seedLine);
      if (seedLine.Id) setContactId(seedLine.Id);
    } else if (snapContactId) {
      setContactId(snapContactId);
    }
    if (Array.isArray(snapOptions)) setAllOptions(snapOptions);

    if (globalOperation !== undefined) dispatch(M_StateActions.removeM_State({ key: 'operation' }));
    if (globalLine !== undefined) dispatch(M_StateActions.removeM_State({ key: 'ContactLine' }));

    fetchOptions(seedOperation, seedLine, { skipDetails: true }).finally(() => {
      if (seedOperation === 'Create') {
        ctx.setIsLoad(false);
      } else if (seedOperation && seedLine?.Id) {
        fetchDetails(seedLine?.Id);
      } else {
        ctx.setIsLoad(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //=====================================================================================================================================
  // Persists the current tab snapshot into M_State.
  // For edit/create, merges current DynamicForm values to keep unsaved edits.
  //=====================================================================================================================================
  // Debounced snapshot to reduce churn
  const saveSnapshotTimer = useRef(null);
  useEffect(() => {
    if (!id) return;

    const currentFormValues = formRef.current?.getValues?.() || {};
    const infoToPersist =
      (operation || '').toLowerCase() === 'view'
        ? contactInfo
        : { ...contactInfo, ...currentFormValues };

    const sessionData = [
      { key: 'contactInfo', value: infoToPersist },
      { key: 'isFirst', value: isFirst },
      { key: 'operation', value: operation },
      { key: 'sessionLine', value: sessionLine },
      { key: 'allOptions', value: allOptions },
      // Also persist the current entity id to avoid leaking across tabs
      { key: 'contactId', value: contactId }
    ];

    if (saveSnapshotTimer.current) clearTimeout(saveSnapshotTimer.current);
    saveSnapshotTimer.current = setTimeout(() => {
      dispatch(M_StateActions.addM_State({ key: id, value: sessionData }));
    }, 80);

    return () => {
      if (saveSnapshotTimer.current) clearTimeout(saveSnapshotTimer.current);
    };
  }, [id, contactInfo, isFirst, operation, sessionLine, allOptions, contactId, dispatch]);

  // =================================================================================================================
  // Respond to active-tab change
  // =================================================================================================================
  useEffect(() => {
    const newId = M_State.find((obj) => obj.key === 'selectedTab')?.value;
    if (newId !== id) {
      setId(newId);
      getSessionStorageData(newId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [M_State]);

  ///=====================================================================================================================================
  // getSessionStorageData
  // Restores a tab snapshot if available; otherwise refetches Options and
  // Details as required by the current mode.
  ///=====================================================================================================================================
  const getSessionStorageData = (sessionID) => {
    ctx.setIsLoad(true);
    const data = M_State.find((obj) => obj.key === sessionID)?.value;
    if (data) {
      const pick = (k) => data.find((x) => x.key === k)?.value;

      const tempInfo = pick('contactInfo');
      const tempIF = pick('isFirst');
      const tempOP = pick('operation');
      const tempSL = pick('sessionLine');
      const tempOptions = pick('allOptions') ?? pick('formSchema'); // backward compatibility
      const tempContactId = pick('contactId');

      setContactInfo(tempInfo || {});
      setIsFirst(tempIF ?? false);
      setOperation(tempOP);
      setSessionLine(tempSL);
      if (tempSL?.Id) setContactId(tempSL.Id);
      else if (tempContactId) setContactId(tempContactId);
      if (Array.isArray(tempOptions)) setAllOptions(tempOptions);
      ctx.setIsLoad(false);
    } else {
      fetchOptions(operation, sessionLine, { skipDetails: true }).finally(() => {
        if (operation !== 'Create' && sessionLine?.Id) {
          fetchDetails(sessionLine?.Id);
        } else {
          ctx.setIsLoad(false);
        }
      });
    }
  };

  // =================================================================================================================
  // Language changes (i18n) - update tab label and refetch options to get translated labels
  // =================================================================================================================
  useEffect(() => {
    if (!operation || isFirst) return; // skip on first load or no operation set
    fetchOptions(operation, sessionLine, { skipDetails: true });

    const key =
      operation === 'Create' ? 'LabelNewContact' : operation === 'Edit' ? 'LabelEditContact' : 'LabelViewContact';
    const name = sessionLine?.Name ?? contactInfo?.Name ?? '';
    const base = t(key);
    const label = operation === 'Create' || !name ? base : `${base} "${name}"`;
    renameTab(String(selectedTab), label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  ///=====================================================================================================================================
  // fetchOptions
  // Fetches raw Options for Contacts.
  // Steps:
  // 1) Call /Options for Contacts and store raw options.
  // 2) Optionally fetch entity details for Edit/View.
  // NOTE: FK lists are NOT fetched here; DynamicForm is responsible for FK sources.
  ///=====================================================================================================================================
  const fetchOptions = async (op, line, { skipDetails } = { skipDetails: false }) => {
    const myToken = tabTokenRef.current;
    ctx.setIsLoad(true);
    try {
      const res = await svcContacts.options();
      if (isFailurePayload(res) || res?.name === 'AxiosError') {
        toastMessages('error', '', apiMessage(res, t('ErrorLoadingData')));
        ctx.setIsLoad(false);
        return;
      }
      if (myToken !== tabTokenRef.current) return; // drop late updates after tab switch
      setAllOptions(res || []);

      // Optionally fetch entity details for Edit/View
      const mode = op || operationRef.current || 'View';
      if (!skipDetails && mode !== 'Create' && (line?.Id || sessionLine?.Id)) {
        await fetchDetails(line?.Id || sessionLine?.Id);
      }
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
    } finally {
      if (myToken === tabTokenRef.current) ctx.setIsLoad(false);
    }
  };

  //=====================================================================================================================================
  // Retrieves entity details by Id for Edit/View and stores them in contactInfo.
  // DynamicForm takes care of hydration and view mirroring.
  //=====================================================================================================================================
  const fetchDetails = async (idToGet) => {
    const myToken = tabTokenRef.current;
    ctx.setIsLoad(true);
    try {
      const res = await svcContacts.getId(idToGet);
      if (isFailurePayload(res)) {
        toastMessages('error', '', apiMessage(res, t('ErrorLoadingData')));
        return;
      }
      if (myToken !== tabTokenRef.current) return;
      setContactInfo(res || {});
      setContactId(res?.Id);
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
    } finally {
      if (myToken === tabTokenRef.current) ctx.setIsLoad(false);
    }
  };

  ///=====================================================================================================================================
  // buildPayloadFromValues
  // Builds DTO payload from values emitted by DynamicForm and raw Options.
  // Steps:
  // 1) Iterate options and choose the value source per field.
  // 2) For FK use Header or NameId key; for booleans coerce to boolean.
  // 3) Lower camel case keys for DTO and assemble object.
  ///=====================================================================================================================================
  const buildPayloadFromValues = (values) => {
    const options = Array.isArray(allOptions) ? allOptions : [];
    const entries = [];

    for (const opt of options) {
      if (!opt) continue;

      const keySource = opt.IsForeignKey ? (opt.Header || opt.Name) : opt.Name;
      const payloadKey = toCamel(opt.Header || keySource);

      let v;
      if (isBoolean(opt)) {
        v = !!values?.[opt.Name];
      } else if (opt.IsForeignKey) {
        const rawId = values?.[keySource];
        v = rawId == null || rawId === '' ? null : Number(rawId);
      } else {
        v = values?.[keySource];
      }

      if (v !== undefined) entries.push([payloadKey, v]);
    }

    return Object.fromEntries(entries);
  };

  ///=====================================================================================================================================
  // onSubmitDynamic
  // Receives values from DynamicForm and performs create or update.
  // Steps:
  // 1) Build DTO payload.
  // 2) Call create or update service according to operation.
  // 3) Toast result and close tab on success.
  ///=====================================================================================================================================
  const onSubmitDynamic = useCallback(
    async (values) => {
      ctx.setIsLoad(true);
      try {
        const payload = buildPayloadFromValues(values);

        const op = (operationRef.current || '').toLowerCase();
        const currentId = contactIdRef.current;

        const res =
          op === 'create'
            ? await svcCreate.post(payload)
            : await svcUpdate.update(currentId, payload);

        if (isFailurePayload(res)) {
          toastMessages('error', '', apiMessage(res, t('ErrorSaving')));
          return;
        }
        toastMessages('success', '', op === 'create' ? t('ContactCreated') : t('ContactUpdated'));
        handleTabClose(idRef.current);
      } catch (err) {
        toastMessages('error', '', apiMessage(err, t('ErrorSaving')));
      } finally {
        ctx.setIsLoad(false);
      }
    },
    [buildPayloadFromValues, ctx, handleTabClose, svcCreate, svcUpdate, t, toastMessages]
  );

  ///=====================================================================================================================================
  // triggerFormSubmit
  // Triggers DynamicForm submit programmatically via imperative ref.
  ///=====================================================================================================================================
  const triggerFormSubmit = () => {
    formRef.current?.submit?.();
  };

  ///=====================================================================================================================================
  // onRefresh
  // Re-fetches Options and details if applicable for current mode.
  // NOTE: No FK fetching here (handled inside DynamicForm).
  ///=====================================================================================================================================
  const onRefresh = () => {
    const op = operationRef.current;
    fetchOptions(op, sessionLine);
  };

  ///=====================================================================================================================================
  // handleFormValuesChange
  // Receives small patches from DynamicForm and persists them under the tab.
  // This lets unsaved edits survive tab switches.
  ///=====================================================================================================================================
  const handleFormValuesChange = useCallback(
    (patch, meta) => {
      if (!patch || !idRef.current) return;

      const prevTabState = M_State.find((o) => o.key === idRef.current)?.value || [];
      const pick = (k) => prevTabState.find((x) => x.key === k)?.value;
      const merged = { ...(pick('contactInfo') || contactInfo || {}), ...patch };

      const updated = [
        ...prevTabState.filter((x) => x.key !== 'contactInfo'),
        { key: 'contactInfo', value: merged }
      ];
      dispatch(M_StateActions.addM_State({ key: idRef.current, value: updated }));
    },
    [M_State, contactInfo, dispatch]
  );

  return (
    <>
      <MHeader
        element={
          <>
            <MButton
              tooltip={t('RefreshTable')}
              icon={<i className="pi pi-refresh iconTable"></i>}
              onClick={onRefresh}
              label={t('RefreshTable')}
            />
            {!isViewMode && (
              <MButton
                tooltip={t('Save')}
                icon={<i className="pi pi-save iconTable"></i>}
                onClick={triggerFormSubmit}
                label={t('Save')}
              />
            )}
          </>
        }
      />

      <div style={{
        padding: '20px',
        background: 'var(--color-white)'
      }}
      >
        <div className="firstDivContainer">
          <DynamicForm
            key={`df-contact-${id}-${operation}-${contactId ?? 'new'}`}
            formRef={formRef}
            mode={(operation || 'View').toLowerCase()}
            schema={allOptions}
            data={contactInfo}
            onSubmit={onSubmitDynamic}
            onValuesChange={handleFormValuesChange}
            title={t('Contact Information')}
            containerClassName="alignInputsUserForm"
          />
        </div>
      </div>
    </>
  );
}

ContactConfigCrud.propTypes = {
  handleTabClose: PropTypes.func,
  toastMessages: PropTypes.func
};
